import { getEmailSignatureHyperlink } from '@common/utils/linkify';
import {
    Box,
    Divider,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Link, Section, Text } from '@react-email/components';
import { type Dispatch, memo, type SetStateAction } from 'react';
import NewProspectStep from './NewProspectStep';
import ToggleSection from './ToggleSection';

interface Props {
    selectedTemplate: number | null;
    isTemplateDetailsLoading: boolean;
    primaryClientFirstName?: string;
    isEmailRemindersEnabled: boolean;
    setIsEmailRemindersEnabled: Dispatch<SetStateAction<boolean>>;
    emailSubject: string;
    emailSubjectError: string;
    handleEmailSubjectChange(value: string): void;
    emailBodyIntroduction: string;
    emailBodyIntroductionError: string;
    handleEmailBodyIntroductionChange(value: string): void;
    emailBodyClosing: string;
    emailBodyClosingError: string;
    handleEmailBodyClosingChange(value: string): void;
    signature: string | null;
    resetErrors(): void;
}

export default memo(function EmailConfiguration({
    selectedTemplate,
    isTemplateDetailsLoading,
    primaryClientFirstName,
    isEmailRemindersEnabled,
    setIsEmailRemindersEnabled,
    emailSubject,
    emailSubjectError,
    handleEmailSubjectChange,
    emailBodyIntroduction,
    emailBodyIntroductionError,
    handleEmailBodyIntroductionChange,
    emailBodyClosing,
    emailBodyClosingError,
    handleEmailBodyClosingChange,
    signature,
    resetErrors,
}: Props) {
    const theme = useTheme();
    const isDisabled = !selectedTemplate || isTemplateDetailsLoading;

    return (
        <NewProspectStep
            stepNumber="3"
            title="Email Configuration"
            description="Configure email settings and notifications"
            showStatus={false}
        >
            <Tooltip
                title={!selectedTemplate ? 'Please select Template first' : ''}
                arrow
                placement="top-end"
            >
                <ToggleSection
                    enabled={isEmailRemindersEnabled}
                    label="Enable Email Reminders"
                    description="Send automated reminder emails to clients for pending tasks"
                    statusText={isEmailRemindersEnabled ? 'Enabled' : 'Disable'}
                    disabled={isDisabled}
                    onToggle={() => {
                        if (isDisabled) return;
                        resetErrors();
                        setIsEmailRemindersEnabled((prev) => !prev);
                    }}
                />
            </Tooltip>
            {selectedTemplate ? (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <Stack spacing={2} sx={{ width: { xs: '100%', md: '50%' } }}>
                        <TextField
                            sx={{ bgcolor: 'neutral.white' }}
                            value={emailSubject}
                            onChange={(event) => handleEmailSubjectChange(event.target.value)}
                            disabled={isDisabled}
                            fullWidth
                            type="text"
                            error={!!emailSubjectError}
                            helperText={emailSubjectError}
                            variant="outlined"
                            label="Subject"
                        />
                        <TextField
                            sx={{ bgcolor: 'neutral.white' }}
                            value={emailBodyIntroduction}
                            onChange={(event) =>
                                handleEmailBodyIntroductionChange(event.target.value)
                            }
                            disabled={isDisabled}
                            fullWidth
                            type="text"
                            multiline
                            rows={6}
                            error={!!emailBodyIntroductionError}
                            helperText={emailBodyIntroductionError}
                            variant="outlined"
                            label="First part of body (before form link)"
                        />
                        <TextField
                            sx={{ bgcolor: 'neutral.white' }}
                            value={emailBodyClosing}
                            onChange={(event) => handleEmailBodyClosingChange(event.target.value)}
                            disabled={isDisabled}
                            fullWidth
                            type="text"
                            multiline
                            rows={6}
                            error={!!emailBodyClosingError}
                            helperText={emailBodyClosingError}
                            variant="outlined"
                            label="Second part of body (after form link)"
                        />
                    </Stack>
                    <Paper
                        variant="outlined"
                        sx={{
                            width: { xs: '100%', md: '50%' },
                            backgroundColor: theme.palette.common.white,
                            borderColor: theme.palette.neutral.borderColor,
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Stack spacing={0}>
                            <Stack
                                sx={{
                                    px: 3,
                                    py: 2,
                                    backgroundColor: theme.palette.neutral.N_50,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={500}>
                                    Intake Email Preview
                                </Typography>
                            </Stack>
                            <Divider />
                            <Stack spacing={0.5} sx={{ px: 3, py: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Subject
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {emailSubject}
                                </Typography>
                            </Stack>
                            <Divider />
                            <Stack sx={{ px: 3, py: 2 }}>
                                <Section>
                                    <Text>Hi {primaryClientFirstName || '[Client Name]'},</Text>
                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                        {emailBodyIntroduction}
                                    </Text>
                                    <Text>
                                        <Link href="#">Intake form link</Link>
                                    </Text>
                                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                                        {emailBodyClosing}
                                    </Text>
                                    <Text
                                        style={{ whiteSpace: 'pre-wrap' }}
                                        dangerouslySetInnerHTML={{
                                            __html: getEmailSignatureHyperlink(signature),
                                        }}
                                    ></Text>
                                </Section>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                        background: theme.palette.neutral.N_50,
                        flex: 1,
                        marginTop: 0,
                        width: '100%',
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                    }}
                >
                    <Typography>Please select a template</Typography>
                </Box>
            )}
        </NewProspectStep>
    );
});
